import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  Divider,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  CheckCircle,
  Timer,
  Factory,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import {
  startOperation,
  pauseOperation,
  completeOperation,
  updateProductionProgress,
  tickTimer,
  clearGateBlockers,
} from '../../../store/slices/operatorSlice';

const ActiveOperation: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { activeOperation, operationElapsedSeconds, isTimerRunning, isLoading, gateBlockers, error } = useSelector(
    (state: RootState) => state.operator
  );

  const [producedQty, setProducedQty] = useState(0);
  const [scrapQty, setScrapQty] = useState(0);
  const [reworkQty, setReworkQty] = useState(0);
  const [notes, setNotes] = useState('');
  const [completeConfirm, setCompleteConfirm] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');

  // Sync local state from store
  useEffect(() => {
    if (activeOperation) {
      setProducedQty(activeOperation.producedQty);
      setScrapQty(activeOperation.scrapQty);
      setReworkQty(activeOperation.reworkQty);
      setNotes(activeOperation.notes || '');
      setCompleteConfirm(false);
    }
  }, [activeOperation?.id]);

  // Tick the timer every second
  useEffect(() => {
    if (!isTimerRunning) return;
    const interval = setInterval(() => {
      dispatch(tickTimer());
    }, 1000);
    return () => clearInterval(interval);
  }, [isTimerRunning, dispatch]);

  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return [h, m, s].map((v) => String(v).padStart(2, '0')).join(':');
  };

  const handleStart = () => {
    if (!activeOperation) return;
    dispatch(startOperation());
  };

  const handlePause = () => {
    if (!activeOperation) return;
    dispatch(pauseOperation());
  };

  const handleSaveProgress = () => {
    if (!activeOperation) return;
    dispatch(
      updateProductionProgress({
        producedQty,
        scrapQty,
        reworkQty,
        notes,
      })
    );
  };

  const handleComplete = () => {
    if (!completeConfirm) {
      setCompleteConfirm(true);
      return;
    }
    if (!activeOperation) return;
    dispatch(
      completeOperation({
        producedQty,
        scrapQty,
        reworkQty,
      })
    );
  };

  const handleOverrideComplete = () => {
    if (!activeOperation || !overrideReason.trim()) return;
    dispatch(
      completeOperation({
        producedQty,
        scrapQty,
        reworkQty,
        overrideQualityGate: true,
        overrideReason: overrideReason.trim(),
      })
    );
    setOverrideReason('');
  };

  if (!activeOperation) {
    return (
      <Card>
        <CardContent>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={6}
          >
            <Factory sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
            <Typography variant="h6" color="textSecondary">
              No Active Operation
            </Typography>
            <Typography variant="body2" color="textSecondary" mt={1}>
              Select a work order from the queue to begin
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const progressPercent =
    activeOperation.plannedQty > 0
      ? Math.min((producedQty / activeOperation.plannedQty) * 100, 100)
      : 0;

  const isStarted = activeOperation.status === 'started';
  const isPaused = activeOperation.status === 'paused';
  const isQueued = activeOperation.status === 'queued';

  return (
    <Card>
      <CardContent>
        {/* Operation Header */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {activeOperation.operationNumber} — {activeOperation.operationDescription}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {activeOperation.productName} ({activeOperation.productCode}) | {activeOperation.workCenter}
            </Typography>
            <Box display="flex" gap={1} mt={1}>
              <Chip
                label={activeOperation.priority.toUpperCase()}
                color={activeOperation.priority === 'high' || activeOperation.priority === 'urgent' ? 'error' : 'warning'}
                size="small"
                variant="outlined"
              />
              <Chip
                label={activeOperation.status.toUpperCase()}
                color={isStarted ? 'warning' : isPaused ? 'info' : 'default'}
                size="small"
              />
            </Box>
          </Box>

          {/* Elapsed Timer */}
          <Box textAlign="center" sx={{ bgcolor: isStarted ? 'warning.light' : 'grey.100', borderRadius: 2, px: 3, py: 2, minWidth: 140 }}>
            <Timer sx={{ fontSize: 28, color: isStarted ? 'warning.dark' : 'action.disabled' }} />
            <Typography variant="h4" fontFamily="monospace" fontWeight={700} color={isStarted ? 'warning.dark' : 'text.secondary'}>
              {formatTime(operationElapsedSeconds)}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Elapsed Time
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Quantity Inputs */}
        <Typography variant="h6" gutterBottom>
          Production Quantities
        </Typography>
        <Grid container spacing={2} mb={2}>
          <Grid item xs={6} sm={3}>
            <Box textAlign="center" p={1.5} bgcolor="info.50" borderRadius={2} border="1px solid" borderColor="info.200">
              <Typography variant="caption" color="textSecondary" display="block">
                Planned
              </Typography>
              <Typography variant="h4" color="info.main" fontWeight={700}>
                {activeOperation.plannedQty}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Produced (Good)"
              type="number"
              value={producedQty}
              onChange={(e) => setProducedQty(Math.max(0, Number(e.target.value)))}
              fullWidth
              size="small"
              inputProps={{ min: 0, max: activeOperation.plannedQty }}
              sx={{
                '& .MuiInputBase-root': { bgcolor: 'success.50' },
              }}
            />
            <Box sx={{ mt: 0.5, height: 6, bgcolor: 'grey.200', borderRadius: 3, overflow: 'hidden' }}>
              <Box
                sx={{
                  height: '100%',
                  width: `${progressPercent}%`,
                  bgcolor: 'success.main',
                  borderRadius: 3,
                  transition: 'width 0.3s',
                }}
              />
            </Box>
            <Typography variant="caption" color="textSecondary">
              {progressPercent.toFixed(0)}% complete
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Scrap"
              type="number"
              value={scrapQty}
              onChange={(e) => setScrapQty(Math.max(0, Number(e.target.value)))}
              fullWidth
              size="small"
              inputProps={{ min: 0 }}
              sx={{
                '& .MuiInputBase-root': { bgcolor: 'error.50' },
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Rework"
              type="number"
              value={reworkQty}
              onChange={(e) => setReworkQty(Math.max(0, Number(e.target.value)))}
              fullWidth
              size="small"
              inputProps={{ min: 0 }}
              sx={{
                '& .MuiInputBase-root': { bgcolor: 'warning.50' },
              }}
            />
          </Grid>
        </Grid>

        {/* Notes */}
        <TextField
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          fullWidth
          multiline
          rows={2}
          size="small"
          placeholder="Any issues, observations, or deviations..."
          sx={{ mb: 3 }}
        />

        {/* Quality gate blockers */}
        {gateBlockers.length > 0 && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => dispatch(clearGateBlockers())}
          >
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Quality gate blocked completion:
            </Typography>
            {gateBlockers.map((blocker, i) => (
              <Typography key={i} variant="body2">
                {blocker}
              </Typography>
            ))}
            <Typography variant="caption" display="block" mt={1}>
              Record the inspection on the Quality tab and get QC sign-off, or override below with a reason
              (logged for audit).
            </Typography>
            <Box display="flex" gap={1} mt={1}>
              <TextField
                size="small"
                fullWidth
                placeholder="Override reason (required, audited)"
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
              />
              <Button
                color="error"
                variant="outlined"
                size="small"
                disabled={!overrideReason.trim() || isLoading}
                onClick={handleOverrideComplete}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Override &amp; Complete
              </Button>
            </Box>
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Action Buttons */}
        {completeConfirm && gateBlockers.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to complete this operation? Produced: {producedQty}, Scrap: {scrapQty}, Rework: {reworkQty}
          </Alert>
        )}

        <Grid container spacing={2}>
          {(isQueued || isPaused) && (
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                color="success"
                fullWidth
                size="large"
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <PlayArrow />}
                onClick={handleStart}
                disabled={isLoading}
                sx={{ py: 2, fontSize: '1.1rem' }}
              >
                START
              </Button>
            </Grid>
          )}
          {isStarted && (
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                color="warning"
                fullWidth
                size="large"
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <Pause />}
                onClick={handlePause}
                disabled={isLoading}
                sx={{ py: 2, fontSize: '1.1rem' }}
              >
                PAUSE
              </Button>
            </Grid>
          )}
          {(isStarted || isPaused) && (
            <Grid item xs={12} sm={4}>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                size="large"
                onClick={handleSaveProgress}
                disabled={isLoading}
                sx={{ py: 2, fontSize: '1.1rem' }}
              >
                SAVE PROGRESS
              </Button>
            </Grid>
          )}
          {(isStarted || isPaused) && (
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                color={completeConfirm ? 'error' : 'primary'}
                fullWidth
                size="large"
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
                onClick={handleComplete}
                disabled={isLoading}
                sx={{ py: 2, fontSize: '1.1rem' }}
              >
                {completeConfirm ? 'CONFIRM COMPLETE' : 'COMPLETE'}
              </Button>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default ActiveOperation;
